/**
 * TaskFormDialog — create / edit an agency task (Story 28.2).
 *
 * §4.1/§4.2 centered modal Dialog (NEVER a Sheet), cloned from AlertFormDialog:
 * Radix dialog, motion overlay bg-black/35, rounded-3xl p-8 max-w-md, staggered
 * fields, reset-on-OPEN (§4.6), close blocked while submitting.
 *
 * Fields (epics.md AC): Title (required) → Description (optional) → Client
 * (optional) → Assignee (optional) → Tags (optional, from the shared tenant
 * vocabulary) → Due date → Recurring (toggle → rule). Priority is set
 * server-side (defaults to "medium") — the field is hidden from the UI for now.
 * Status is NOT edited here — it is changed inline on the page (the dedicated
 * PATCH /:id/status path so 28.3 recurrence has one hook).
 *
 * Saving POSTs (create) or PATCHes (edit) /api/tasks; a 403 UpgradeRequiredError
 * surfaces the UpgradeLimitModal (defense-in-depth; the page also guards the
 * Create button with useUpgradeGuard). The task limit is TENANT-WIDE (28.4).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input, Label, Switch } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import {
  PLAN_LIMITS,
  SAFETY_CAPS,
  getMinimumPlanForFeature,
  type Plan,
} from "@provenleap/types";
import { apiPost, apiPatch } from "../../lib/apiClient";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import { UpgradeLimitModal } from "../billing/UpgradeLimitModal";
import { DialogSelect } from "../clients/DialogSelect";
import { countTasksForGate } from "../../lib/taskPlanGate";
import { RECURRENCE_OPTIONS } from "./taskDisplay";
import type { Task, TaskRecurrenceRule, TaskTag } from "./types";
import { TaskTagPicker } from "./TaskTagPicker";

export interface TaskFormClientOption {
  id: string;
  name: string;
  /** Drives the gate's demo exclusion (server getTaskCount parity). */
  demoMode?: boolean;
}

export interface TaskFormAssigneeOption {
  id: string;
  name: string;
}

interface TaskGateModalState {
  currentPlan: Plan;
  currentUsage: number;
  currentLimit: number;
  upgradePlan?: Plan;
  nextTierLimit: number | null;
}

interface UpgradeProblem {
  type?: string;
  current_plan?: Plan;
  current_limit?: number;
  current_usage?: number;
  upgrade_to?: Plan;
  next_tier_limit?: number | null;
}

function isUpgradeProblem(
  err: unknown,
): err is { status: number } & UpgradeProblem {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { status?: number }).status === 403 &&
    (err as { type?: string }).type ===
      "https://provenleap.com/errors/upgrade-required"
  );
}

function gateFromProblem(p: UpgradeProblem): TaskGateModalState {
  return {
    currentPlan: p.current_plan ?? "free",
    currentUsage: p.current_usage ?? 0,
    currentLimit: p.current_limit ?? 0,
    upgradePlan: p.upgrade_to,
    nextTierLimit: p.next_tier_limit ?? null,
  };
}

/** Tenant-wide gate state (mirrors the server feature-guard math for taskLimit). */
function buildGate(plan: Plan, count: number): TaskGateModalState | null {
  const limit = PLAN_LIMITS[plan].taskLimit;
  if (limit === null) {
    const cap = SAFETY_CAPS.taskLimit;
    if (cap !== undefined && count >= cap) {
      return {
        currentPlan: plan,
        currentUsage: count,
        currentLimit: cap,
        upgradePlan: undefined,
        nextTierLimit: null,
      };
    }
    return null;
  }
  if (count < limit) return null;
  const upgrade = getMinimumPlanForFeature("taskLimit", count + 1);
  const isMax = upgrade === plan;
  const nextLimit = isMax ? null : PLAN_LIMITS[upgrade].taskLimit;
  return {
    currentPlan: plan,
    currentUsage: count,
    currentLimit: limit,
    upgradePlan: isMax ? undefined : upgrade,
    nextTierLimit: typeof nextLimit === "number" ? nextLimit : null,
  };
}

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active clients for the optional Client selector. */
  clients: TaskFormClientOption[];
  /** Tenant team members for the optional Assignee selector. */
  assignees: TaskFormAssigneeOption[];
  /** When set, the dialog is in EDIT mode and pre-fills from this task. */
  task?: Task | null;
  /** Loaded tasks — feeds the client-side gate count. */
  existingTasks?: Task[];
  onSaved?: () => void;
  /** Page-level toast (forwarded to the tag picker for create-tag errors). */
  onToast?: (type: "success" | "error" | "info", message: string) => void;
}

interface FormState {
  title: string;
  description: string;
  clientId: string;
  assignedTo: string;
  tags: TaskTag[];
  dueDate: string; // "YYYY-MM-DD" or ""
  isRecurring: boolean;
  recurrenceRule: TaskRecurrenceRule;
}

function emptyState(): FormState {
  return {
    title: "",
    description: "",
    clientId: "",
    assignedTo: "",
    tags: [],
    dueDate: "",
    isRecurring: false,
    recurrenceRule: "weekly",
  };
}

function msToDateInput(ms: number | null): string {
  if (ms === null) return "";
  return new Date(ms).toISOString().slice(0, 10);
}

function stateFromTask(task: Task): FormState {
  return {
    title: task.title,
    description: task.description ?? "",
    clientId: task.clientId ?? "",
    assignedTo: task.assignedTo ?? "",
    tags: task.tags,
    dueDate: msToDateInput(task.dueDate),
    isRecurring: task.isRecurring,
    recurrenceRule: task.recurrenceRule ?? "weekly",
  };
}

export function TaskFormDialog({
  open,
  onOpenChange,
  clients,
  assignees,
  task,
  existingTasks = [],
  onSaved,
  onToast,
}: TaskFormDialogProps) {
  const queryClient = useQueryClient();
  const { plan } = usePlanLimits();
  const isEdit = !!task;
  const [form, setForm] = useState<FormState>(() => emptyState());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [gateModal, setGateModal] = useState<TaskGateModalState | null>(null);

  // Reset on OPEN (never on close — §4.6).
  useEffect(() => {
    if (!open) return;
    setForm(task ? stateFromTask(task) : emptyState());
    setErrors({});
  }, [open, task]);

  const clientSelectOptions = useMemo(
    () => [
      { value: "", label: "No client (general task)" },
      ...clients.map((cl) => ({ value: cl.id, label: cl.name })),
    ],
    [clients],
  );
  const assigneeSelectOptions = useMemo(
    () => [
      { value: "", label: "Unassigned" },
      ...assignees.map((a) => ({ value: a.id, label: a.name })),
    ],
    [assignees],
  );
  function patch(next: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...next }));
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        clientId: form.clientId || null,
        assignedTo: form.assignedTo || null,
        tagIds: form.tags.map((t) => t.id),
        dueDate: form.dueDate
          ? new Date(`${form.dueDate}T00:00:00Z`).getTime()
          : null,
        isRecurring: form.isRecurring,
        recurrenceRule: form.isRecurring ? form.recurrenceRule : null,
      };
      const res = isEdit
        ? await apiPatch(`/api/tasks/${task!.id}`, payload)
        : await apiPost("/api/tasks", payload);
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onOpenChange(false);
      onSaved?.();
    },
    onError: (err) => {
      if (isUpgradeProblem(err)) setGateModal(gateFromProblem(err));
    },
  });

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (form.title.length > 500) e.title = "Title must be 500 characters or fewer";
    if (form.description.length > 2000)
      e.description = "Description must be 2000 characters or fewer";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;

    // Client-side gate (CREATE only). The task limit is TENANT-WIDE on every
    // tier, so the count is plan-independent (countTasksForGate). The server 403
    // fallback above still applies (defense-in-depth on a stale list).
    if (!isEdit) {
      const clientsForGate = clients.map((c) => ({
        id: c.id,
        demoMode: !!c.demoMode,
      }));
      const count = countTasksForGate(existingTasks, clientsForGate);
      if (count !== undefined) {
        const gate = buildGate(plan, count);
        if (gate) {
          setGateModal(gate);
          return;
        }
      }
    }
    mutation.mutate();
  }

  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return; // close blocked while pending
    onOpenChange(next);
  }

  let fieldIndex = 0;
  const nextDelay = () => 0.05 * ++fieldIndex;

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      {isEdit ? "Edit Task" : "Create a Task"}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {isEdit
                        ? "Update this task's details."
                        : "Track internal work for your team and clients."}
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                    {/* Title */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="task-title">Title</Label>
                      <Input
                        id="task-title"
                        className="rounded-2xl"
                        value={form.title}
                        onChange={(e) => patch({ title: e.target.value })}
                        placeholder="e.g. Send monthly report"
                        maxLength={500}
                      />
                      {errors.title && (
                        <p className="text-[12px] text-destructive">
                          {errors.title}
                        </p>
                      )}
                    </motion.div>

                    {/* Description */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="task-description">Description</Label>
                      <textarea
                        id="task-description"
                        className="w-full rounded-2xl bg-background px-3 py-2 text-[13px] text-foreground shadow-xs outline-none resize-none placeholder:text-muted-foreground"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        rows={3}
                        value={form.description}
                        onChange={(e) => patch({ description: e.target.value })}
                        placeholder="Optional details…"
                        maxLength={2000}
                      />
                      {errors.description && (
                        <p className="text-[12px] text-destructive">
                          {errors.description}
                        </p>
                      )}
                    </motion.div>

                    {/* Client + Assignee */}
                    <motion.div
                      className="grid grid-cols-2 gap-3"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="task-client">Client</Label>
                        <DialogSelect
                          id="task-client"
                          ariaLabel="Client"
                          value={form.clientId}
                          onChange={(v) => patch({ clientId: v })}
                          options={clientSelectOptions}
                          placeholder="No client…"
                          searchPlaceholder="Search clients…"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="task-assignee">Assignee</Label>
                        <DialogSelect
                          id="task-assignee"
                          ariaLabel="Assignee"
                          value={form.assignedTo}
                          onChange={(v) => patch({ assignedTo: v })}
                          options={assigneeSelectOptions}
                          placeholder="Unassigned…"
                          searchPlaceholder="Search members…"
                        />
                      </div>
                    </motion.div>

                    {/* Tags */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label>Tags</Label>
                      <TaskTagPicker
                        value={form.tags}
                        onChange={(tags) => patch({ tags })}
                        onToast={onToast}
                      />
                    </motion.div>

                    {/* Due date */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="task-due">Due date</Label>
                      <Input
                        id="task-due"
                        type="date"
                        className="rounded-2xl"
                        value={form.dueDate}
                        onChange={(e) => patch({ dueDate: e.target.value })}
                      />
                    </motion.div>

                    {/* Recurring */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <div className="flex items-center justify-between">
                        <Label htmlFor="task-recurring">Recurring task</Label>
                        <Switch
                          id="task-recurring"
                          checked={form.isRecurring}
                          onCheckedChange={(v) => patch({ isRecurring: v })}
                        />
                      </div>
                      {form.isRecurring && (
                        <DialogSelect
                          id="task-recurrence"
                          ariaLabel="Recurrence"
                          value={form.recurrenceRule}
                          onChange={(v) =>
                            patch({ recurrenceRule: v as TaskRecurrenceRule })
                          }
                          options={RECURRENCE_OPTIONS}
                        />
                      )}
                    </motion.div>

                    {mutation.isError && !isUpgradeProblem(mutation.error) && (
                      <p className="text-[12px] text-destructive" role="alert">
                        {(mutation.error as { detail?: string; title?: string })
                          ?.detail ??
                          (mutation.error as { title?: string })?.title ??
                          "Failed to save task"}
                      </p>
                    )}

                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        onClick={() => handleOpenChange(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="brand"
                        className="rounded-2xl text-[13px] cursor-pointer"
                        disabled={mutation.isPending}
                      >
                        {mutation.isPending
                          ? "Saving…"
                          : isEdit
                            ? "Save changes"
                            : "Create task"}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      <UpgradeLimitModal
        open={gateModal !== null}
        onOpenChange={(o) => !o && setGateModal(null)}
        feature="taskLimit"
        currentPlan={gateModal?.currentPlan ?? plan}
        currentUsage={gateModal?.currentUsage ?? 0}
        currentLimit={gateModal?.currentLimit ?? 0}
        upgradePlan={gateModal?.upgradePlan}
        nextTierLimit={gateModal?.nextTierLimit ?? null}
        billingInterval="monthly"
      />
    </>
  );
}
